import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGlobalUserPermission } from "@shared/hooks/useGlobalUserPermission";

// =====================================================
// TYPE DEFINITIONS
// =====================================================

/**
 * Represents a bento menu item displayed in the module selector
 */
interface BentoMenuItem {
  link: string; // Primary navigation route
  lucideIcon: string; // Lucide icon name
  name: string; // Display name from API
  description: string; // Description from API
  module: string; // Module key
  active: boolean; // Whether item is active
}

/**
 * Represents a single navigation item in the sidenav
 */
interface NavigationItem {
  link: string; // Navigation route
  label: string; // Display label
  shortLabel: string; // Abbreviated label
  active: string; // Active state indicator
  isLink: boolean; // Whether item is navigatable
  iconName: string; // Icon name for display
}

/**
 * Represents a navigation group containing related items
 */
interface NavigationGroup {
  type: string; // Group type (always "group")
  label: string; // Group display name
  items: NavigationItem[]; // Items within the group
}

/**
 * Main navigation context interface
 */
interface NavigationContextType {
  currentModule: string; // Currently active module
  setCurrentModule: (module: string) => void; // Function to change module
  navigationItems: NavigationGroup[]; // Sidenav navigation structure
  dynamicBentoMenuItems: BentoMenuItem[]; // Bento menu items
  availableModules: string[]; // All accessible modules
}

// =====================================================
// CONTEXT SETUP
// =====================================================

const NavigationContext = createContext<NavigationContextType | undefined>(
  undefined
);

const MODULE_QUERY_KEY = "currentModule";
const STORAGE_KEY = "smart360_current_module";

// =====================================================
// NAVIGATION PROVIDER COMPONENT
// =====================================================

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  // React Query and routing setup
  const queryClient = useQueryClient();
  const location = useLocation();
  const manuallySetRef = useRef(false); // Prevents infinite loops in module detection

  // API data fetching
  const permissionData = useGlobalUserPermission();
  console.log("permissionData", JSON.stringify(permissionData, null, 2));

  const permissionLandingModule = permissionData?.data?.userInfo?.landingModule;
  console.log("permissionLandingModule", permissionLandingModule);
  const userModules = permissionData?.data?.permissions?.modules || {};

  console.log("userModules in NavigationProvider:", userModules);

  // =====================================================
  // MEMOIZED VALUES
  // =====================================================

  /**
   * Extract all available module keys from API response
   * Updates when userModules changes
   */
  const availableModules = useMemo(() => {
    return Object.keys(userModules);
  }, [userModules]);

  // =====================================================
  // HELPER FUNCTIONS
  // =====================================================

  /**
   * Ensures route has leading slash for consistency
   * @param route - Route string to format
   * @returns Formatted route with leading slash
   */
  const formatRoute = (route: string): string => {
    return route.startsWith("/") ? route : `/${route}`;
  };

  /**
   * Simple module detection based on URL prefix
   * @param path - Current URL pathname
   * @returns Module key that matches the path, or null if no match
   */

  const userInfo = permissionData?.data?.userInfo;
  const userRoles = userInfo?.roleCodes || []; // full array of roles
  console.log("userRoles:", userRoles);
  const detectModuleFromPath = (path: string): string | null => {
    const cleanPath = path.split("?")[0]; // Remove query parameters

    // Normalize roles (case-insensitive)
    const normalizedRoles = userRoles.map((r) => r.toLowerCase());
    console.log("Normalized Roles:", normalizedRoles);

    // Roles that should treat plan-tarrif as Utility Setup
    const userSpecificRole = [
      "utility administrator",
      "tenant admin",
      "system admin",
      "utility manager",
      "cso manager",
      "billing manager",
    ];

    // Simple prefix matching - no loops needed
    if (cleanPath.startsWith("/mx") || cleanPath.startsWith("mx")) return "MX";
    if (cleanPath.startsWith("/cx") || cleanPath.startsWith("cx")) return "CX";
    if (
      cleanPath.startsWith("/call-center") ||
      cleanPath.startsWith("call-center")
    )
      return "Call Center";

    if (cleanPath.startsWith("/bx") || cleanPath.startsWith("bx")) return "BX";
    if (cleanPath.startsWith("/wx") || cleanPath.startsWith("wx")) return "WX";
    if (cleanPath.startsWith("/rx") || cleanPath.startsWith("rx")) return "RX";
    if (cleanPath.startsWith("/ax") || cleanPath.startsWith("ax")) return "AX";
    // if (cleanPath.startsWith("/utility") || cleanPath.startsWith("utility"))
    //   return "Utility Setup";

    if (cleanPath.startsWith("/utility") || cleanPath.startsWith("utility")) {
      // ✅ Only Tenant Admin or System Admin should map to Onboarding
      if (
        normalizedRoles.some(
          (role) => role === "tenant admin" || role === "system admin"
        )
      ) {
        return "Onboarding";
      }
      return "Utility Setup";
    }

    if (
      cleanPath.startsWith("/onboarding/data-migration") ||
      cleanPath.startsWith("onboarding/data-migration")
    )
      return "Utility Setup";

    if (
      cleanPath.startsWith("/onboarding") ||
      cleanPath.startsWith("onboarding")
    )
      return "Onboarding";

    if (
      cleanPath.startsWith("/plan-tarrif") ||
      cleanPath.startsWith("plan-tarrif")
    ) {
      // ✅ Check if user role matches any of the special roles
      const hasUtilityRole = userSpecificRole.some((role) =>
        normalizedRoles.includes(role.toLowerCase())
      );
      return hasUtilityRole ? "Utility Setup" : "Plans & tariff";
    }

    if (
      cleanPath.startsWith("/automation") ||
      cleanPath.startsWith("/communication-hub")
    )
      return "AUTOMATION";
    if (cleanPath.startsWith("/integrations")) return "Utility Setup";

    if (cleanPath.startsWith("/service-orders")) return "WX";
    if (cleanPath.startsWith("/reports-analytics")) return "RX";
    if (cleanPath.startsWith("/integrations")) return "Onboarding";

    return null;
  };

  /**
   * Finds the first available route in a module's submodules
   * Used for determining default navigation target for bento menu
   * @param submodules - Module's submodule structure from API
   * @returns First found route or null if none available
   */
  const findFirstRoute = (submodules: any): string | null => {
    // Iterate through categories (MANAGEMENT, LIFECYCLE, etc.)
    for (const categoryKey of Object.keys(submodules)) {
      const category = submodules[categoryKey];

      // Iterate through submodules within category
      for (const submoduleKey of Object.keys(category)) {
        const submodule = category[submoduleKey];

        // Return first route found
        // if (submodule?.routes && submodule.routes.length > 0) {
        //   return submodule.routes[0];
        // }

        //returrn default route
        if (submodule?.defaultRoute) {
          return submodule.defaultRoute;
        }
      }
    }
    return null;
  };

  // =====================================================
  // MODULE MANAGEMENT
  // =====================================================

  /**
   * Determines initial module based on:
   * 1. Stored module (if still available)
   * 2. Current URL path detection
   * 3. Landing module from user preferences
   * 4. First available module as fallback
   */
  const getInitialModule = (): string => {
    console.log("Getting initial module...");

    // Skip URL detection if no modules available yet
    if (!userModules || Object.keys(userModules).length === 0) {
      console.log("No modules available yet, using fallback");
      return permissionLandingModule || "";
    }

    // Try stored module first
    const storedModule = localStorage.getItem(STORAGE_KEY);
    if (storedModule && availableModules.includes(storedModule)) {
      console.log("Using stored module:", storedModule);
      return storedModule;
    }

    // Try to detect module from current path
    const detectedModule = detectModuleFromPath(location.pathname);
    if (detectedModule) {
      console.log("Using detected module from path:", detectedModule);
      localStorage.setItem(STORAGE_KEY, detectedModule);
      return detectedModule;
    }

    // Fallback to landing module or first available
    const fallbackModule = permissionLandingModule || availableModules[0] || "";
    console.log("Using fallback module:", fallbackModule);

    // Store the determined module
    if (fallbackModule) {
      localStorage.setItem(STORAGE_KEY, fallbackModule);
    }

    return fallbackModule;
  };

  /**
   * React Query for current module state management
   * Provides persistent module selection across app usage
   */
  const { data: currentModule = getInitialModule() } = useQuery<string>({
    queryKey: [MODULE_QUERY_KEY],
    queryFn: getInitialModule,
    staleTime: Infinity, // Module doesn't change often
  });

  /**
   * Updates current module and persists to localStorage
   * @param module - Module key to set as current
   */
  const setCurrentModule = (module: string) => {
    console.log(`Setting current module to: ${module}`);
    manuallySetRef.current = true; // Prevent auto-detection interference
    queryClient.setQueryData([MODULE_QUERY_KEY], module);
    localStorage.setItem(STORAGE_KEY, module);
  };

  // =====================================================
  // URL-BASED MODULE DETECTION
  // =====================================================

  /**
   * Auto-detects module changes based on URL navigation
   * Updates current module when user navigates to different module's routes
   */
  useEffect(() => {
    // Skip if module was manually set (prevents loops)
    if (manuallySetRef.current) {
      console.log("Skipping auto-detection: module was manually set");
      manuallySetRef.current = false;
      return;
    }

    // Skip if no modules loaded yet
    if (!userModules || Object.keys(userModules).length === 0) {
      console.log("Skipping auto-detection: no modules loaded yet");
      return;
    }

    // Auto-detect module from current path
    const detectedModule = detectModuleFromPath(location.pathname);
    console.log(
      "Auto-detected module from path:",
      detectedModule,
      "Current module:",
      currentModule
    );

    // Update module if detection finds different module
    if (detectedModule && detectedModule !== currentModule) {
      console.log(
        `Switching module from ${currentModule} to ${detectedModule}`
      );
      setCurrentModule(detectedModule);
    }
  }, [location.pathname, currentModule, availableModules, userModules]);

  // =====================================================
  // DYNAMIC MENU GENERATION
  // =====================================================

  /**
   * Generates bento menu items from all available modules
   * Each item represents a module with its basic info and default route
   */
  const dynamicBentoMenuItems = useMemo((): BentoMenuItem[] => {
    // Return empty if no modules available
    if (!userModules || Object.keys(userModules).length === 0) {
      console.log("No modules available for bento menu");
      return [];
    }

    console.log(
      "Generating Bento Menu Items from userModules:",
      Object.keys(userModules)
    );
    const menuItems: BentoMenuItem[] = [];

    // Process each module to create bento menu items
    Object.keys(userModules).forEach((moduleKey) => {
      const moduleData = userModules[moduleKey];
      const moduleInfo = moduleData.moduleInfo;
      const submodules = moduleData.submodules;

      console.log(`Processing module: ${moduleKey}`, {
        hasModuleInfo: !!moduleInfo,
        hasName: !!moduleInfo?.name,
        hasDescription: !!moduleInfo?.description,
        hasSubmodules: !!submodules,
      });

      // Only create menu item if module has required data
      if (
        moduleInfo &&
        moduleInfo.name &&
        moduleInfo.description &&
        submodules
      ) {
        const firstRoute = findFirstRoute(submodules);
        console.log(`First route for ${moduleKey}:`, firstRoute);

        if (firstRoute) {
          const formattedRoute = formatRoute(firstRoute);

          const menuItem = {
            link: formattedRoute,
            lucideIcon: moduleInfo.iconName || "Settings", // Fallback icon
            name: moduleInfo.name,
            description: moduleInfo.description,
            module: moduleKey,
            active: true,
          };

          console.log(`Created menu item for ${moduleKey}:`, menuItem);
          menuItems.push(menuItem);
        } else {
          console.log(`No routes found for module: ${moduleKey}`);
        }
      } else {
        console.log(`Skipping module ${moduleKey} - missing required data`);
      }
    });

    // Sort menu items by display order, then alphabetically
    menuItems.sort((a, b) => {
      const moduleA = userModules[a.module];
      const moduleB = userModules[b.module];
      const orderA = moduleA?.moduleInfo?.displayOrder || 999;
      const orderB = moduleB?.moduleInfo?.displayOrder || 999;

      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return a.name.localeCompare(b.name);
    });

    console.log(
      "Final Bento Menu Items:",
      menuItems.map((item) => `${item.module}: ${item.name} -> ${item.link}`)
    );
    return menuItems;
  }, [userModules]);

  // =====================================================
  // DYNAMIC NAVIGATION GENERATION
  // =====================================================

  /**
   * Generates sidenav navigation structure for current module
   * Creates groups based on API categories (MANAGEMENT, LIFECYCLE, etc.)
   * Each submodule becomes a navigation item within appropriate group
   */
  const navigationItems = useMemo((): NavigationGroup[] => {
    // Return empty if no current module or module data
    if (!userModules || !currentModule || !userModules[currentModule]) {
      return [];
    }

    const currentModuleData = userModules[currentModule];
    const submodules = currentModuleData.submodules;

    if (!submodules) return [];

    const navigationGroups: NavigationGroup[] = [];

    // Process each category within the current module
    Object.keys(submodules).forEach((categoryKey) => {
      const category = submodules[categoryKey];
      const items: NavigationItem[] = [];

      // Convert each submodule to navigation item
      Object.keys(category).forEach((submoduleKey) => {
        const submodule = category[submoduleKey];

        // Only create nav item if submodule has routes
        if (submodule && submodule.routes && submodule.routes.length > 0) {
          const firstRoute = submodule.defaultRoute;
          const formattedRoute = formatRoute(firstRoute);

          items.push({
            link: formattedRoute,
            label: submoduleKey,
            shortLabel: submoduleKey,
            active: "", // Will be set by sidenav component
            isLink: true,
            iconName: submodule.iconName || "file-text", // Fallback icon
          });
        }
      });

      // Only create group if it has navigation items
      if (items.length > 0) {
        // Sort items within group by display order
        items.sort((a, b) => {
          const submoduleA = category[a.label];
          const submoduleB = category[b.label];
          const orderA = submoduleA?.displayOrder || 999;
          const orderB = submoduleB?.displayOrder || 999;
          return orderA - orderB;
        });

        // Create navigation group with proper label
        navigationGroups.push({
          type: "group",
          label: categoryKey,
          items: items,
        });
      }
    });
    console.log(
      "Navigation Groups for module",
      currentModule,
      ":",
      navigationGroups
    );
    return navigationGroups;
  }, [userModules, currentModule, location.pathname]);

  // =====================================================
  // CONTEXT PROVIDER
  // =====================================================

  return (
    <NavigationContext.Provider
      value={{
        currentModule,
        setCurrentModule,
        navigationItems,
        dynamicBentoMenuItems,
        availableModules,
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

// =====================================================
// CUSTOM HOOK
// =====================================================

/**
 * Hook to access navigation context
 * Throws error if used outside NavigationProvider
 * @returns NavigationContextType
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};
